import { AlertCircle, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { AssistantActionButton } from "./AssistantActionButton";
import { AssistantInsightPreview, parseInsightBuckets } from "./AssistantInsightPreview";
import { CustomFieldConfirmCard } from "./CustomFieldConfirmCard";
import type { ActionButton, ChatLine, CustomFieldProposal } from "./types";

// Bubble renderer for user / assistant / system / error lines.
// - User: right-aligned blue bubble, plain text.
// - System: small muted hint line, plain text.
// - Error: alert-style card with icon + accent border. Shown when the
//   backend SSE stream emits an error event OR a slash command fails.
//   Replaces the empty assistant placeholder so we don't paint a hollow
//   reply bubble next to the error.
// - Assistant: markdown via react-markdown. When opsApplied > 0 a small
//   sparkles badge sits on the top-right of the bubble. When the body
//   looks like a JSON-encoded insight payload (see parseInsightBuckets),
//   an inline AssistantInsightPreview renders in place of the raw text.
//   Click-to-confirm action chips render below the bubble when the line
//   has actions[].

interface Props {
    line: ChatLine;
    onActionClick?: (action: ActionButton) => void;
    /** Phase 4C — confirm handler for a `propose_custom_field` ActionButton.
        Renders a confirm card instead of the thin chip; on confirm it runs the
        two ede.create commands + view re-compose. */
    onCustomFieldConfirm?: (proposal: CustomFieldProposal) => Promise<void>;
}

export function AssistantMessageLine({ line, onActionClick, onCustomFieldConfirm }: Props) {
    if (line.role === "user") {
        return (
            <div className="assistant-line assistant-line--user" data-testid="assistant-line-user">
                <div className="assistant-line__bubble">{line.body}</div>
            </div>
        );
    }
    if (line.role === "system") {
        return (
            <div className="assistant-line assistant-line--system" data-testid="assistant-line-system">
                {line.body}
            </div>
        );
    }
    if (line.role === "error") {
        return (
            <div className="assistant-line assistant-line--error" data-testid="assistant-line-error" role="alert">
                <span className="assistant-line__error-icon" aria-hidden="true">
                    <AlertCircle size={14} />
                </span>
                <div className="assistant-line__error-body">
                    <div className="assistant-line__error-title">Something went wrong</div>
                    <div className="assistant-line__error-message">{line.body}</div>
                </div>
            </div>
        );
    }

    // assistant — check for an inline insight payload before falling
    // through to markdown rendering. Empty body (no text yet, no insight,
    // no actions) means the SSE turn is still in progress — render an
    // animated "thinking" indicator inside the bubble so the user sees
    // that something is happening between sending the message and the
    // first text_delta landing.
    const insight = parseInsightBuckets(line.body);
    const isThinking = line.body === "" && !insight && (line.actions?.length ?? 0) === 0;

    return (
        <div className="assistant-line assistant-line--assistant" data-testid="assistant-line-assistant">
            <div
                className={`assistant-line__bubble${line.opsApplied > 0 ? " assistant-line__bubble--ops-applied" : ""}`}
            >
                {line.opsApplied > 0 && (
                    <span
                        className="assistant-line__ops-badge"
                        title={`${line.opsApplied} change${line.opsApplied === 1 ? "" : "s"} applied`}
                        data-testid="assistant-line-ops-badge"
                    >
                        <Sparkles size={10} />
                    </span>
                )}
                {isThinking ? (
                    <span className="assistant-line__thinking" data-testid="assistant-line-thinking">
                        <span>Thinking</span>
                        <span className="assistant-line__thinking-dots" aria-hidden="true">
                            <span className="assistant-line__thinking-dot" />
                            <span className="assistant-line__thinking-dot" />
                            <span className="assistant-line__thinking-dot" />
                        </span>
                    </span>
                ) : insight ? (
                    <AssistantInsightPreview
                        title={insight.title}
                        groupbyField={insight.groupbyField}
                        totalCount={insight.totalCount}
                        buckets={insight.buckets}
                    />
                ) : (
                    <div className="assistant-line__markdown">
                        <ReactMarkdown
                            components={{
                                // Compact prose — the bubble is small; trim default margins.
                                p: ({ children }) => <p className="assistant-md__p">{children}</p>,
                                ul: ({ children }) => <ul className="assistant-md__ul">{children}</ul>,
                                ol: ({ children }) => <ol className="assistant-md__ol">{children}</ol>,
                                li: ({ children }) => <li className="assistant-md__li">{children}</li>,
                                code: ({ children }) => <code className="assistant-md__code">{children}</code>,
                                pre: ({ children }) => <pre className="assistant-md__pre">{children}</pre>,
                                a: ({ children, href }) => (
                                    <a className="assistant-md__a" href={href} target="_blank" rel="noopener noreferrer">
                                        {children}
                                    </a>
                                ),
                            }}
                        >
                            {line.body}
                        </ReactMarkdown>
                    </div>
                )}
            </div>
            {line.actions && line.actions.length > 0 && (
                <div className="assistant-line__actions" data-testid="assistant-line-actions">
                    {line.actions.map((action, i) =>
                        action.kind === "propose_custom_field" ? (
                            <CustomFieldConfirmCard
                                key={`ccf-${i}`}
                                action={action}
                                disabled={!onCustomFieldConfirm}
                                onConfirm={onCustomFieldConfirm ?? (async () => {})}
                            />
                        ) : (
                            <AssistantActionButton
                                key={`${action.kind}-${i}`}
                                action={action}
                                onClick={(a) => onActionClick?.(a)}
                            />
                        ),
                    )}
                </div>
            )}
        </div>
    );
}
